import { getScheduleEntries, getTeacherById, getTeachers, addTeacher, deleteTeacher, getGroupById, getGroups, addGroup, updateGroup, deleteGroup, getStudents, addStudent, updateGroupPreferences, updateTeacher, updateTeacherPreferences, getSubjects, getSubjectById, addSubject, updateSubject, deleteSubject, getRooms, getRoomById, addRoom, updateRoom, deleteRoom, getDrafts, saveDraft as saveDraftApi, loadDraft as loadDraftApi, deleteDraft as deleteDraftApi, publishDraftPeriod as publishDraftPeriodApi, createScheduleEntry, updateScheduleEntry, deleteScheduleEntry, getYearStructure, saveYearStructure, getInstitutionSettings } from './apiService';
import logger from '../utils/logger';
import ScheduleEntry from '../models/ScheduleEntry';
import Preference, { RawPreferenceData } from '../models/Preference';
import Teacher, { normalizeTeacherTagsFromApi } from '../models/Teacher';
import Group from '../models/Group';
import Student from '../models/Student';
import Subject from '../models/Subject';
import Period from '../models/Period';
import Room from '../models/Room';
import AcademicYearService from './AcademicYearService';

// It seems convertToScheduleEntries is a local helper in ScheduleDisplay.jsx.
// We might need to move it or replicate its logic if it's complex.
// For now, let's assume a simplified direct mapping or that ScheduleEntry constructor handles it.

/**
 * Converts raw schedule entries from API to ScheduleEntry instances.
 * @param {any[]} rawEntries - Raw entries from API.
 * @returns {Array<ScheduleEntry>} - Array of ScheduleEntry instances.
 */
const convertToScheduleEntries = (rawEntries: any[]) => {
    if (!rawEntries || !Array.isArray(rawEntries)) {
        logger.warn("[AssetManager] convertToScheduleEntries: Input is not a valid array. Returning empty array.");
        return [];
    }

    return rawEntries.map(rawEntry => {
        try {
            return new ScheduleEntry(rawEntry);
        } catch (error) {
            logger.error('[AssetManager] Error converting raw entry to ScheduleEntry:', error);
            logger.error('[AssetManager] Raw entry data:', rawEntry);
            // Return a minimal valid entry to prevent crashes
            return new ScheduleEntry({
                id: rawEntry.id || 'unknown',
                course: rawEntry.course || 'Unknown Course',
                room: rawEntry.room || 'Unknown Room',
                day: rawEntry.day || 'Monday',
                timeStart: rawEntry.timeStart || rawEntry.start_time || '09:00',
                timeEnd: rawEntry.timeEnd || rawEntry.end_time || '10:00'
            });
        }
    });
};


class AssetManager {
    private static instance: AssetManager | null = null;
    private institutionSettingsCache: any = null;
    private institutionSettingsPromise: Promise<any> | null = null;
    private academicYearService: AcademicYearService;

    constructor() {
        this.academicYearService = AcademicYearService.getInstance();
    }

    /**
     * Get the singleton instance of AssetManager
     * @returns {AssetManager} The singleton instance
     */
    static getInstance(): AssetManager {
        if (!AssetManager.instance) {
            AssetManager.instance = new AssetManager();
        }
        return AssetManager.instance;
    }

    /**
     * Initializes the academic year service cache by fetching the year structure.
     * This should be called early in the application lifecycle to ensure
     * synchronous week-to-year mapping is available.
     * @returns {Promise<void>}
     */
    async initializeAcademicYearCache(): Promise<void> {
        try {
            logger.debug('[AssetManager] Initializing academic year cache...');
            await this.academicYearService.getAcademicYearStructure();
            logger.debug('[AssetManager] Academic year cache initialized successfully');
        } catch (error) {
            logger.warn('[AssetManager] Failed to initialize academic year cache:', error);
        }
    }

    /**
     * Fetches institution settings from the API and caches them.
     * Also initializes the academic year cache for proper week-to-year mapping.
     * Subsequent calls will return the cached version without making API calls.
     * Uses a singleton pattern to prevent multiple simultaneous API calls.
     * @returns {Promise<any>} - A promise that resolves to the institution settings object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchInstitutionSettings() {
        // Return cached settings if available
        if (this.institutionSettingsCache !== null) {

            return this.institutionSettingsCache;
        }

        // If there's already a pending request, wait for it instead of making a new one
        if (this.institutionSettingsPromise !== null) {
            logger.debug('[AssetManager] Waiting for existing institution settings request...');
            return this.institutionSettingsPromise;
        }

        logger.debug('[AssetManager] Fetching institution settings from API...');
        
        // Create a new promise for this request
        this.institutionSettingsPromise = this._fetchInstitutionSettingsInternal();
        
        try {
            const result = await this.institutionSettingsPromise;
            return result;
        } finally {
            // Clear the promise once it's resolved (success or failure)
            this.institutionSettingsPromise = null;
        }
    }

    /**
     * Internal method that actually fetches the institution settings.
     * This is separated to allow the singleton pattern in the public method.
     * @returns {Promise<any>} - A promise that resolves to the institution settings object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    private async _fetchInstitutionSettingsInternal() {
        try {
            const settings = await getInstitutionSettings();
            
            // Map snake_case API response to camelCase for consistency
            const mappedSettings = {
                ...settings,
                urlEndpoint: settings.url_endpoint,
                minHour: settings.min_hour || 8,
                maxHour: settings.max_hour || 18,
                // Map day fields from snake_case to camelCase
                activeMonday: settings.active_monday !== undefined ? settings.active_monday : true,
                activeTuesday: settings.active_tuesday !== undefined ? settings.active_tuesday : true,
                activeWednesday: settings.active_wednesday !== undefined ? settings.active_wednesday : true,
                activeThursday: settings.active_thursday !== undefined ? settings.active_thursday : true,
                activeFriday: settings.active_friday !== undefined ? settings.active_friday : true,
                activeSaturday: settings.active_saturday !== undefined ? settings.active_saturday : false,
                activeSunday: settings.active_sunday !== undefined ? settings.active_sunday : false
            };

            // Cache the settings
            this.institutionSettingsCache = mappedSettings;
            logger.debug('[AssetManager] Institution settings fetched and cached:', mappedSettings);

            // Initialize academic year cache while we have an active connection
            await this.initializeAcademicYearCache();

            return mappedSettings;
        } catch (err) {
            logger.error('[AssetManager] Error fetching institution settings:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load institution settings via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Clears the cached institution settings and academic year cache.
     */
    clearInstitutionSettingsCache() {
        logger.debug('[AssetManager] Clearing institution settings cache');
        this.institutionSettingsCache = null;
        this.institutionSettingsPromise = null;
        this.academicYearService.clearCache();
    }

    /**
     * Get active days based on cached institution settings
     * @returns {Promise<Array>} Promise that resolves to array of active day names (lowercase)
     */
    async getActiveDays(): Promise<string[]> {
        try {
            const institutionSettings = await this.fetchInstitutionSettings();
            
            if (!institutionSettings) {
                logger.warn('[AssetManager] No institution settings available, returning default days');
                return [
                'monday',
                'tuesday',
                'wednesday',
                'thursday',
                'friday'
                ];
            }

            const activeDays: string[] = [];
            const dayMapping = {
                'monday': institutionSettings.activeMonday,
                'tuesday': institutionSettings.activeTuesday,
                'wednesday': institutionSettings.activeWednesday,
                'thursday': institutionSettings.activeThursday,
                'friday': institutionSettings.activeFriday,
                'saturday': institutionSettings.activeSaturday,
                'sunday': institutionSettings.activeSunday
            };

            Object.entries(dayMapping).forEach(([day, isActive]) => {
                if (isActive) {
                    activeDays.push(day);
                }
            });

            // Fallback to default days if no active days found
            const result = activeDays.length > 0 ? activeDays : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
            return result;
        } catch (error) {
            logger.error('[AssetManager] Error getting active days:', error);
            return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']; // Return default days on error
        }
    }

    /**
     * Get active days capitalized for display
     * @returns {Promise<Array>} Promise that resolves to array of active day names (capitalized)
     */
    async getActiveDaysCapitalized(): Promise<string[]> {
        try {
            const activeDays = await this.getActiveDays();
            return activeDays.map(day => day.charAt(0).toUpperCase() + day.slice(1));
        } catch (error) {
            logger.error('[AssetManager] Error getting active days capitalized:', error);
            return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => day.charAt(0).toUpperCase() + day.slice(1));
        }
    }

    /**
     * Check if a specific day is active
     * @param {string} day - The day name (lowercase)
     * @returns {Promise<boolean>} Promise that resolves to true if the day is active
     */
    async isDayActive(day: string): Promise<boolean> {
        try {
            const activeDays = await this.getActiveDays();
            return activeDays.includes(day.toLowerCase());
        } catch (error) {
            logger.error('[AssetManager] Error checking if day is active:', error);
            return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(day.toLowerCase()); // Default behavior on error
        }
    }

    /**
     * Get active days synchronously from cached institution settings
     * This should only be used when you're sure the institution settings are already cached
     * @returns {Array} Array of active day names (lowercase)
     */
    getActiveDaysSync(): string[] {
        if (!this.institutionSettingsCache) {
            logger.warn('[AssetManager] Institution settings not cached, returning default days');
            return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        }

        const activeDays: string[] = [];
        const dayMapping = {
            'monday': this.institutionSettingsCache.activeMonday,
            'tuesday': this.institutionSettingsCache.activeTuesday,
            'wednesday': this.institutionSettingsCache.activeWednesday,
            'thursday': this.institutionSettingsCache.activeThursday,
            'friday': this.institutionSettingsCache.activeFriday,
            'saturday': this.institutionSettingsCache.activeSaturday,
            'sunday': this.institutionSettingsCache.activeSunday
        };

        Object.entries(dayMapping).forEach(([day, isActive]) => {
            if (isActive) {
                activeDays.push(day);
            }
        });

        // Fallback to default days if no active days found
        return activeDays.length > 0 ? activeDays : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    }

    /**
     * Fetches schedule entries from the API and transforms them into ScheduleEntry objects.
     * Ensures academic year cache is initialized for proper week-to-year mapping.
     * @param {Object} filters - Optional filters to apply to the API call.
     * @param {string} include - Optional include parameter for related data.
     * @returns {Promise<Array<ScheduleEntry>>} - A promise that resolves to an array of ScheduleEntry objects.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchScheduleEntries(filters = {}, include = '') {
        logger.debug('[AssetManager] Fetching schedule entries...');
        try {
            // Ensure academic year cache is initialized before processing schedule entries
            await this.initializeAcademicYearCache();

            const rawEntries = await getScheduleEntries(filters);
            logger.debug('[AssetManager] Received raw entries:', rawEntries?.length || 0);

            if (rawEntries && Array.isArray(rawEntries)) {
                // The transformation logic from ScheduleDisplay.jsx is now part of convertToScheduleEntries
                const scheduleEntryObjects = convertToScheduleEntries(rawEntries);
                logger.debug('[AssetManager] Transformed to ScheduleEntry objects:', scheduleEntryObjects.length);
                return scheduleEntryObjects;
            } else {
                logger.warn('[AssetManager] No entries received or data is not an array. Returning empty array.');
                return [];
            }
        } catch (err) {
            logger.error('[AssetManager] Error fetching or processing schedule entries:', err);
            // Re-throw the error so the caller can handle it (e.g., update UI with error state)
            const errorMessage = err instanceof Error ? err.message : 'Failed to load schedule entries via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches a teacher and returns a Teacher instance.
     * @param {string|number} teacherId - The teacher ID to fetch.
     * @returns {Promise<Teacher>} - Promise that resolves to a Teacher instance.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchTeacher(teacherId: string | number) {
        logger.debug('[AssetManager] Fetching teacher...');
        try {
            const teacherData = await getTeacherById(teacherId);
            return new Teacher(teacherData);
        } catch (err) {
            logger.error('[AssetManager] Error fetching or processing teacher:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load teacher via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches group preferences and returns a Group instance with preferences populated.
     * @param {string|number} groupId - The group ID to fetch preferences for.
     * @returns {Promise<Group>} - Promise that resolves to a Group instance with preferences.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchGroupPreferences(groupId: string | number) {
        logger.debug('[AssetManager] Fetching group preferences...');
        try {
            const groupData = await getGroupById(groupId, 'preferences');
            return Group.fromApiData(groupData);
        } catch (err) {
            logger.error('[AssetManager] Error fetching or processing group preferences:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load group preferences via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches all teachers from the API and transforms them into Teacher objects.
     * @returns {Promise<Array<Teacher>>} - A promise that resolves to an array of Teacher objects.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchTeachers() {
        try {
            const teachersData = await getTeachers('preferences,qualifiedSubjects');
            if (teachersData && Array.isArray(teachersData)) {
                const teachers = teachersData.map((teacherData: any) => new Teacher(teacherData));
                logger.debug('[AssetManager] Transformed to Teacher objects:', teachers.length);
                return teachers;
            }
            return [];
        } catch (err) {
            logger.error('[AssetManager] Error fetching or processing teachers:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load teachers via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Creates a teacher and returns the created Teacher instance.
     * @param {any} teacherData - The teacher data to create.
     * @returns {Promise<Teacher>} - Promise that resolves to the created Teacher instance.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async createTeacher(teacherData: any) {
        logger.debug('[AssetManager] Creating teacher...');
        try {
            const newTeacherData = await addTeacher(teacherData);
            return new Teacher(newTeacherData);
        } catch (err) {
            console.error('[AssetManager] Error creating teacher:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to create teacher via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches all groups from the API and transforms them into Group objects.
     * @param {string} include - Optional include parameter for related data (e.g., 'students,preferences').
     * @returns {Promise<Array<Group>>} - A promise that resolves to an array of Group objects.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchGroups(include = 'students,preferences') {
        logger.debug('[AssetManager] Fetching groups...');
        try {
            const groupsData = await getGroups(include);
            if (groupsData && Array.isArray(groupsData)) {
                const groups = Group.fromApiDataArray(groupsData);
                logger.debug('[AssetManager] Transformed to Group objects:', groups.length);
                return groups;
            }
            return [];
        } catch (err) {
            console.error('[AssetManager] Error fetching or processing groups:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load groups via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches a group by ID from the API and transforms it into a Group object.
     * @param {string|number} groupId - The ID of the group.
     * @param {string} include - Optional include parameter for related data (e.g., 'students,preferences').
     * @returns {Promise<Group>} - A promise that resolves to a Group object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchGroup(groupId: string | number, include = 'students,preferences') {
        logger.debug('[AssetManager] Fetching group...');
        try {
            const groupData = await getGroupById(groupId, include);
            if (groupData) {
                const group = Group.fromApiData(groupData);
                logger.debug('[AssetManager] Transformed to Group object with', group.students.length, 'students and', group.preferences.length, 'preferences');
                return group;
            }
            throw new Error('Group not found');
        } catch (err) {
            console.error('[AssetManager] Error fetching or processing group:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load group via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Creates a new group in the system.
     * @param {Object} groupData - The data for the new group.
     * @returns {Promise<Group>} - A promise that resolves to the newly created Group object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async createGroup(groupData: any) {
        logger.debug('[AssetManager] Creating group...');
        try {
            const newGroupData = await addGroup(groupData);
            if (newGroupData) {
                const group = Group.fromApiData(newGroupData);
                logger.debug('[AssetManager] Created new group:', group);
                return group;
            }
            throw new Error('Failed to create group: No data returned from API');
        } catch (err) {
            console.error('[AssetManager] Error creating group:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to create group via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Updates an existing group in the system.
     * @param {Object} groupData - The updated group data.
     * @returns {Promise<Group>} - A promise that resolves to the updated Group object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async updateGroup(groupData: any) {
        logger.debug('[AssetManager] Updating group...');
        try {
            const updatedGroupData = await updateGroup(groupData.id, groupData);
            if (updatedGroupData) {
                const group = Group.fromApiData(updatedGroupData);
                logger.debug('[AssetManager] Updated group:', group);
                return group;
            }
            throw new Error('Failed to update group: No data returned from API');
        } catch (err) {
            console.error('[AssetManager] Error updating group:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to update group via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Updates an existing group's core details and its preferences.
     * @param {Group} groupData - The group data including ID, name, group_code, and preferences.
     * @returns {Promise<Group>} - A promise that resolves to the updated Group object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async updateGroupAndPreferences(groupData: any) {
        logger.debug('[AssetManager] Updating group and preferences for group ID:', groupData.id);
        try {
            // Step 1: Update core group details (name, group_code)
            // The existing updateGroup API service function expects specific fields.
            const coreGroupDetailsToUpdate = {
                name: groupData.name,
                group_code: groupData.group_code,
                ...(groupData.hasOwnProperty('tags') ? { tags: groupData.tags } : {})
            };
            // updateGroup from apiService returns the full group object, potentially with old preferences
            let updatedGroupDataFromServer = await updateGroup(groupData.id, coreGroupDetailsToUpdate);
            logger.debug('[AssetManager] Core group details updated:', updatedGroupDataFromServer);

            // Step 2: Update preferences if they exist
            if (groupData.preferences && Array.isArray(groupData.preferences)) {
                logger.debug('[AssetManager] Updating preferences:', groupData.preferences);
                // updateGroupPreferences from apiService expects an array of preference objects
                // and returns the updated array of preference objects (not the full group).
                const updatedPreferencesArray = await updateGroupPreferences(groupData.id, groupData.preferences);
                logger.debug('[AssetManager] Preferences updated, API returned:', updatedPreferencesArray);
                // Combine the updated core details with the updated preferences
                // The updatedGroupDataFromServer contains the latest core details.
                // We replace its preferences with the ones returned by updateGroupPreferences.
                // The Preference model's fromRawDataArray will convert API response (if needed) to Preference instances.
                updatedGroupDataFromServer = {
                    ...updatedGroupDataFromServer,
                    preferences: Preference.fromRawDataArray(updatedPreferencesArray)
                };
            } else {
                logger.debug('[AssetManager] No preferences to update or preferences are not in expected format.');
                // If there were no preferences to update, ensure the preferences array in the returned
                // object is what was returned by the core update, or an empty array if it was undefined.
                updatedGroupDataFromServer.preferences = updatedGroupDataFromServer.preferences || [];
            }

            if (updatedGroupDataFromServer) {
                // Convert the final combined data to a Group model instance
                const group = Group.fromApiData(updatedGroupDataFromServer);
                logger.debug('[AssetManager] Successfully updated group and preferences:', group);
                return group;
            }
            throw new Error('Failed to update group and/or preferences: No definitive data returned from API');
        } catch (err) {
            console.error('[AssetManager] Error updating group and preferences:', err);
            // Ensure a more specific error message if possible
            const errorMessage = (err as any)?.response?.data?.message || (err instanceof Error ? err.message : 'Failed to update group and preferences via AssetManager.');
            throw new Error(errorMessage);
        }
    }

    /**
     * Deletes a group from the system.
     * @param {string|number} groupId - The ID of the group to delete.
     * @returns {Promise<void>} - A promise that resolves when the group is deleted.
     * @throws {Error} - Throws an error if the API call fails.
     */
    async deleteGroup(groupId: string | number) {
        logger.debug('[AssetManager] Deleting group...');
        try {
            await deleteGroup(groupId);
            logger.debug('[AssetManager] Group deleted successfully');
        } catch (err) {
            console.error('[AssetManager] Error deleting group:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete group via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches students from the API and transforms them into Student objects.
     * @param {Object} filters - Optional filters to apply to the API call.
     * @param {string} include - Optional include parameter for related data.
     * @returns {Promise<Array<Student>>} - A promise that resolves to an array of Student objects.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchStudents(filters = {}, include = '') {
        logger.debug('[AssetManager] Fetching students...');
        try {
            const studentsData = await getStudents(filters, include);
            if (studentsData && Array.isArray(studentsData)) {
                const students = Student.fromApiDataArray(studentsData);
                logger.debug('[AssetManager] Transformed to Student objects:', students.length);
                return students;
            }
            return [];
        } catch (err) {
            console.error('[AssetManager] Error fetching or processing students:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load students via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Creates a new student in the system.
     * @param {Object} studentData - The data for the new student.
     * @returns {Promise<Student>} - A promise that resolves to the newly created Student object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async createStudent(studentData: any) {
        logger.debug('[AssetManager] Creating student...');
        try {
            const newStudentData = await addStudent(studentData);
            if (newStudentData) {
                const student = Student.fromApiData(newStudentData);
                logger.debug('[AssetManager] Created new student:', student);
                return student;
            }
            throw new Error('Failed to create student: No data returned from API');
        } catch (err) {
            console.error('[AssetManager] Error creating student:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to create student via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Updates an existing teacher's core details and its preferences.
     * @param {Teacher} teacherData - The teacher data including ID and other updatable fields, and preferences.
     * @returns {Promise<Teacher>} - A promise that resolves to the updated Teacher object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async updateTeacher(teacherData: any) {
        logger.debug('[AssetManager] Updating teacher and preferences for teacher ID:', teacherData.id);
        try {
            // Step 1: Update core teacher details
            // Adjust the fields being updated based on your Teacher model and API capabilities
            const coreTeacherDetailsToUpdate = {
                name: teacherData.name,
                email: teacherData.email,
                tags: normalizeTeacherTagsFromApi(teacherData.tags),
                // Add any other core, updatable fields from the Teacher model here
                // e.g., office: teacherData.office, phone: teacherData.phone
            };

            // Call the API service to update core teacher details
            // Assumes 'updateTeacher' exists in apiService and returns the updated teacher object (potentially without full preference details)
            let updatedTeacherDataFromServer = await updateTeacher(teacherData.id, coreTeacherDetailsToUpdate);
            logger.debug('[AssetManager] Core teacher details updated:', updatedTeacherDataFromServer);

            // Step 2: Update preferences if they exist and are provided
            if (teacherData.preferences && Array.isArray(teacherData.preferences)) {
                logger.debug('[AssetManager] Updating preferences for teacher ID:', teacherData.id, teacherData.preferences);
                
                // Convert preferences to the format expected by the API
                // The API expects snake_case properties (start_time, end_time)
                const preferencesForApi = teacherData.preferences.map((pref: any) => {
                    // If it's already a Preference instance, convert to raw data
                    if (pref.toRawData && typeof pref.toRawData === 'function') {
                        return pref.toRawData();
                    }
                    // If it's already raw data with snake_case, use as is
                    if (pref.start_time && pref.end_time) {
                        return pref;
                    }
                    // If it's raw data with camelCase, convert to snake_case
                    if (pref.startTime && pref.endTime) {
                        return {
                            id: pref.id,
                            day: pref.day,
                            start_time: pref.startTime,
                            end_time: pref.endTime,
                            status: pref.status,
                            createdAt: pref.createdAt,
                            updatedAt: pref.updatedAt
                        };
                    }
                    // Fallback: return as is
                    return pref;
                });
                
                logger.debug('[AssetManager] Formatted preferences for API:', preferencesForApi);
                
                // Assumes 'updateTeacherPreferences' exists in apiService,
                // takes teacher ID and an array of preference objects (or raw preference data),
                // and returns the updated array of preference objects (as raw data from API).
                const updatedPreferencesArray = await updateTeacherPreferences(teacherData.id, preferencesForApi);
                logger.debug('[AssetManager] Preferences updated, API returned:', updatedPreferencesArray);

                // Combine the updated core details with the updated preferences
                // The Preference.fromRawDataArray will convert API response to Preference instances.
                updatedTeacherDataFromServer = {
                    ...updatedTeacherDataFromServer,
                    preferences: Preference.fromRawDataArray(updatedPreferencesArray)
                };
            } else {
                logger.debug('[AssetManager] No preferences to update or preferences are not in expected format for teacher ID:', teacherData.id);
                // Ensure the preferences array in the returned object is what was returned by the core update,
                // or an empty array if it was undefined.
                updatedTeacherDataFromServer.preferences = updatedTeacherDataFromServer.preferences || [];
            }

            if (updatedTeacherDataFromServer) {
                // Convert the final combined data to a Teacher model instance
                const teacher = new Teacher(updatedTeacherDataFromServer);
                logger.debug('[AssetManager] Successfully updated teacher and preferences:', teacher);
                return teacher;
            }
            throw new Error('Failed to update teacher and/or preferences: No definitive data returned from API');
        } catch (err) {
            console.error('[AssetManager] Error updating teacher and preferences:', err);
            const errorMessage = (err as any)?.response?.data?.message || (err instanceof Error ? err.message : 'Failed to update teacher and preferences via AssetManager.');
            throw new Error(errorMessage);
        }
    }

    /**
     * Deletes a teacher from the system.
     * @param {string|number} teacherId - The ID of the teacher to delete.
     * @returns {Promise<void>} - A promise that resolves when the teacher is deleted.
     * @throws {Error} - Throws an error if the API call fails.
     */
    async deleteTeacher(teacherId: string | number) {
        logger.debug('[AssetManager] Deleting teacher...');
        try {
            await deleteTeacher(teacherId);
            logger.debug('[AssetManager] Teacher deleted successfully');
        } catch (err) {
            console.error('[AssetManager] Error deleting teacher:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete teacher via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches subjects from the API and transforms them into Subject objects.
     * @returns {Promise<Array<Subject>>} - A promise that resolves to an array of Subject objects.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchSubjects() {
        logger.debug('[AssetManager] Fetching subjects from API...');
        try {
            const subjectsData = await getSubjects();
            if (subjectsData && Array.isArray(subjectsData)) {
                const subjects = Subject.fromRawDataArray(subjectsData);
                logger.debug('[AssetManager] Transformed to Subject objects:', subjects.length);
                return subjects;
            }
            return [];
        } catch (err) {
            console.error('[AssetManager] Error fetching or processing subjects:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load subjects via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches a subject by ID from the API and transforms it into a Subject object.
     * @param {string|number} subjectId - The ID of the subject.
     * @returns {Promise<Subject>} - A promise that resolves to a Subject object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchSubject(subjectId: string | number) {
        logger.debug('[AssetManager] Fetching subject...');
        try {
            const subjectData = await getSubjectById(subjectId);
            if (subjectData) {
                const subject = new Subject(subjectData);
                logger.debug('[AssetManager] Transformed to Subject object:', subject);
                return subject;
            }
            throw new Error('Subject not found');
        } catch (err) {
            console.error('[AssetManager] Error fetching or processing subject:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load subject via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Creates a new subject in the system.
     * @param {Object} subjectData - The data for the new subject.
     * @returns {Promise<Subject>} - A promise that resolves to the newly created Subject object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async createSubject(subjectData: any) {
        logger.debug('[AssetManager] Creating subject...');
        try {
            const newSubjectData = await addSubject(subjectData);
            if (newSubjectData) {
                const subject = new Subject(newSubjectData);
                logger.debug('[AssetManager] Created new subject:', subject);
                return subject;
            }
            throw new Error('Failed to create subject: No data returned from API');
        } catch (err) {
            console.error('[AssetManager] Error creating subject:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to create subject via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Updates an existing subject.
     * @param {string|number} subjectId - The ID of the subject to update.
     * @param {Object} updatedData - The updated data for the subject.
     * @returns {Promise<Subject>} - A promise that resolves to the updated Subject object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async updateSubject(subjectId: string | number, updatedData: any) {
        logger.debug('[AssetManager] Updating subject...');
        try {
            const updatedSubjectData = await updateSubject(subjectId, updatedData);
            if (updatedSubjectData) {
                const subject = new Subject(updatedSubjectData);
                logger.debug('[AssetManager] Updated subject:', subject);
                return subject;
            }
            throw new Error('Failed to update subject: No data returned from API');
        } catch (err) {
            console.error('[AssetManager] Error updating subject:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to update subject via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Deletes a subject from the system.
     * @param {string|number} subjectId - The ID of the subject to delete.
     * @returns {Promise<void>} - A promise that resolves when the subject is deleted.
     * @throws {Error} - Throws an error if the API call fails.
     */
    async deleteSubject(subjectId: string | number) {
        logger.debug('[AssetManager] Deleting subject...');
        try {
            await deleteSubject(subjectId);
            logger.debug('[AssetManager] Subject deleted successfully');
        } catch (err) {
            console.error('[AssetManager] Error deleting subject:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete subject via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches rooms from the API and transforms them into Room objects.
     * @returns {Promise<Array<Room>>} - A promise that resolves to an array of Room objects.
     */
    async fetchRooms() {
        logger.debug('[AssetManager] Fetching rooms from API...');
        try {
            const roomsData = await getRooms();
            if (roomsData && Array.isArray(roomsData)) {
                const rooms = Room.fromRawDataArray(roomsData);
                logger.debug('[AssetManager] Transformed to Room objects:', rooms.length);
                return rooms;
            }
            return [];
        } catch (err) {
            console.error('[AssetManager] Error fetching or processing rooms:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load rooms via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches the year structure using the AcademicYearService for caching.
     * This ensures we only fetch the year structure once and cache it appropriately.
     * @returns {Promise<any>} - A promise that resolves to the year structure object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchYearStructure() {
        logger.debug('[AssetManager] Fetching year structure via AcademicYearService...');
        try {
            // Use AcademicYearService which handles caching automatically
            const yearStructure = await this.academicYearService.getAcademicYearStructure();
            
            if (yearStructure) {
                return yearStructure;
            } else {
                console.warn('[AssetManager] No year structure available');
                // Return a default structure if none exists
                const currentYear = new Date().getFullYear();
                return {
                    academicYear: `${currentYear}-${currentYear + 1}`,
                    startWeek: 1,
                    endWeek: 52,
                    periods: [],
                    holidays: []
                };
            }
        } catch (err) {
            console.error('[AssetManager] Error fetching year structure:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load year structure via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    // --- PERIODS ---

    /**
     * Fetches periods from the year structure for the current institution.
     * Uses the cached year structure via fetchYearStructure() for consistency.
     * @returns {Promise<Array<Period>>} - A promise that resolves to an array of Period objects.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchPeriods() {
        logger.debug('[AssetManager] Fetching periods from cached year structure...');
        try {
            const yearStructure = await this.fetchYearStructure() as any;
            if (yearStructure && yearStructure.periods && Array.isArray(yearStructure.periods)) {
                const periods = yearStructure.periods.map((periodData: any) => {
                    // Convert year structure period format to Period object
                    const periodObj = new Period({
                        id: periodData.id,
                        name: periodData.name,
                        start_week: periodData.startWeek,
                        end_week: periodData.endWeek,
                        start_date: periodData.startDate,
                        end_date: periodData.endDate,
                        color: periodData.color || '#3B82F6',
                        year: yearStructure.academicYear ? parseInt(yearStructure.academicYear.split('-')[0]) : new Date().getFullYear(),
                        is_active: true
                    });
                    return periodObj;
                });
                
                return periods;
            }

            return [];
        } catch (err) {
            console.error('[AssetManager] Error fetching periods from year structure:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load periods from year structure.';
            if (errorMessage.includes('404')) {
                logger.debug('[AssetManager] No year structure found, returning empty periods array');
                return [];
            }
            throw new Error(errorMessage);
        }
    }

    /**
     * Fetches a specific period by ID from the cached year structure.
     * @param {string|number} periodId - The ID of the period.
     * @returns {Promise<Period>} - A promise that resolves to a Period object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async fetchPeriod(periodId: string | number) {
        logger.debug('[AssetManager] Fetching period from cached year structure...');
        try {
            const periods = await this.fetchPeriods();
            const period = periods.find((p: any) => String(p.id) === String(periodId));
            if (period) {
                logger.debug('[AssetManager] Found period in cached year structure');
                return period;
            }
            throw new Error('Period not found in year structure');
        } catch (err) {
            console.error('[AssetManager] Error fetching period from year structure:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load period from year structure.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Creates a new period by updating the year structure.
     * Uses cached year structure for consistency.
     * @param {Object} periodData - The data for the new period (name, start_week, end_week).
     * @returns {Promise<Period>} - A promise that resolves to the newly created Period object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async createPeriod(periodData: any) {
        logger.debug('[AssetManager] Creating period via cached year structure...');
        try {
            // First, get the current year structure using cached method
            let currentYearStructure: any;
            try {
                currentYearStructure = await this.fetchYearStructure() as any;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : '';
                if (errorMessage.includes('404')) {
                    // No year structure exists yet, create a basic one
                    const currentYear = new Date().getFullYear();
                    currentYearStructure = {
                        academicYear: `${currentYear}-${currentYear + 1}`,
                        startWeek: 1,
                        endWeek: 52,
                        periods: [],
                        holidays: []
                    };
                } else {
                    throw err;
                }
            }

            // Add the new period to the existing periods
            const newPeriod = {
                name: periodData.name,
                startWeek: periodData.start_week,
                endWeek: periodData.end_week,
                color: periodData.color || '#3B82F6',
                startDate: periodData.start_date,
                endDate: periodData.end_date
            };

            const updatedYearStructure = {
                ...currentYearStructure,
                periods: [...(currentYearStructure.periods || []), newPeriod]
            };

            // Save the updated year structure
            logger.debug('[AssetManager] Saving updated year structure with new period...');
            const result = await saveYearStructure(updatedYearStructure) as any;
            
            // Clear the cache since we updated the year structure
            this.academicYearService.clearCache();
            logger.debug('[AssetManager] Cleared academic year cache after creating period');
            
            if (result && result.yearStructure && result.yearStructure.periods) {
                // Find the newly created period (it should have an ID now)
                const createdPeriod = result.yearStructure.periods.find((p: any) => p.name === periodData.name);
                if (createdPeriod) {
                    const period = new Period({
                        id: createdPeriod.id,
                        name: createdPeriod.name,
                        start_week: createdPeriod.startWeek,
                        end_week: createdPeriod.endWeek,
                        start_date: createdPeriod.startDate,
                        end_date: createdPeriod.endDate,
                        color: createdPeriod.color || '#3B82F6',
                        year: currentYearStructure.academicYear ? parseInt(currentYearStructure.academicYear.split('-')[0]) : new Date().getFullYear(),
                        is_active: true
                    });
                    logger.debug('[AssetManager] Period created successfully via year structure');
                    return period;
                }
            }
            throw new Error('Failed to create period in year structure');
        } catch (err) {
            console.error('[AssetManager] Error creating period via year structure:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to create period via year structure.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Updates an existing period by updating the year structure.
     * Uses cached year structure for consistency.
     * @param {string|number} periodId - The ID of the period to update.
     * @param {Object} updatedData - The updated period data.
     * @returns {Promise<Period>} - A promise that resolves to the updated Period object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async updatePeriod(periodId: string | number, updatedData: any) {
        logger.debug('[AssetManager] Updating period via cached year structure...');
        try {
            // Get the current year structure using cached method
            const currentYearStructure = await this.fetchYearStructure() as any;
            if (!currentYearStructure || !currentYearStructure.periods) {
                throw new Error('No year structure found');
            }

            // Find and update the period
            const updatedPeriods = currentYearStructure.periods.map((period: any) => {
                if (String(period.id) === String(periodId)) {
                    return {
                        ...period,
                        ...(updatedData.name && { name: updatedData.name }),
                        ...(updatedData.start_week && { startWeek: updatedData.start_week }),
                        ...(updatedData.end_week && { endWeek: updatedData.end_week }),
                        ...(updatedData.color && { color: updatedData.color }),
                        ...(updatedData.start_date && { startDate: updatedData.start_date }),
                        ...(updatedData.end_date && { endDate: updatedData.end_date })
                    };
                }
                return period;
            });

            const updatedYearStructure = {
                ...currentYearStructure,
                periods: updatedPeriods
            };

            // Save the updated year structure
            logger.debug('[AssetManager] Saving updated year structure...');
            const result = await saveYearStructure(updatedYearStructure) as any;
            
            // Clear the cache since we updated the year structure
            this.academicYearService.clearCache();
            logger.debug('[AssetManager] Cleared academic year cache after updating period');
            
            if (result && result.yearStructure && result.yearStructure.periods) {
                const updatedPeriod = result.yearStructure.periods.find((p: any) => String(p.id) === String(periodId));
                if (updatedPeriod) {
                    const period = new Period({
                        id: updatedPeriod.id,
                        name: updatedPeriod.name,
                        start_week: updatedPeriod.startWeek,
                        end_week: updatedPeriod.endWeek,
                        start_date: updatedPeriod.startDate,
                        end_date: updatedPeriod.endDate,
                        color: updatedPeriod.color || '#3B82F6',
                        year: currentYearStructure.academicYear ? parseInt(currentYearStructure.academicYear.split('-')[0]) : new Date().getFullYear(),
                        is_active: true
                    });
                    logger.debug('[AssetManager] Period updated successfully via year structure');
                    return period;
                }
            }
            throw new Error('Failed to update period in year structure');
        } catch (err) {
            console.error('[AssetManager] Error updating period via year structure:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to update period via year structure.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Deletes a period by updating the year structure.
     * Uses cached year structure for consistency.
     * @param {string|number} periodId - The ID of the period to delete.
     * @returns {Promise<void>} - A promise that resolves when the period is deleted.
     * @throws {Error} - Throws an error if the API call fails.
     */
    async deletePeriod(periodId: string | number) {
        logger.debug('[AssetManager] Deleting period via cached year structure...');
        try {
            // Get the current year structure using cached method
            const currentYearStructure = await this.fetchYearStructure() as any;
            if (!currentYearStructure || !currentYearStructure.periods) {
                throw new Error('No year structure found');
            }

            // Remove the period
            const filteredPeriods = currentYearStructure.periods.filter((period: any) => String(period.id) !== String(periodId));

            const updatedYearStructure = {
                ...currentYearStructure,
                periods: filteredPeriods
            };

            // Save the updated year structure
            logger.debug('[AssetManager] Saving year structure without deleted period...');
            await saveYearStructure(updatedYearStructure);
            
            // Clear the cache since we updated the year structure
            this.academicYearService.clearCache();
            logger.debug('[AssetManager] Cleared academic year cache after deleting period');
            logger.debug('[AssetManager] Period deleted successfully via year structure');
        } catch (err) {
            console.error('[AssetManager] Error deleting period via year structure:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete period via year structure.';
            throw new Error(errorMessage);
        }
    }

    // --- Draft Management Methods ---

    async fetchDrafts() {
        logger.debug('[AssetManager] Fetching available drafts...');
        try {
            const { getDrafts } = await import('./apiService.js');
            const drafts = await getDrafts();
            logger.debug('[AssetManager] Received drafts:', drafts?.length || 0);
            return drafts || [];
        } catch (err) {
            console.error('[AssetManager] Error fetching drafts:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch drafts via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    async saveDraft(draftName: string, year: number, overwrite = false) {
        logger.debug('[AssetManager] Saving draft:', { draftName, year, overwrite });
        try {
            const result = await saveDraftApi(draftName, year, overwrite);
            logger.debug('[AssetManager] Draft saved successfully:', result);
            return result;
        } catch (err) {
            console.error('[AssetManager] Error saving draft:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to save draft via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    async loadDraft(draftName: string, year: number, weekRange: { startWeek: number; endWeek: number } | null = null) {
        logger.debug('[AssetManager] Loading draft:', { draftName, year, weekRange });
        try {
            // Cast to any because apiService.js is JS and the third arg is optional
            const result = await (loadDraftApi as any)(draftName, year, weekRange);
            logger.debug('[AssetManager] Draft loaded successfully:', result);
            return result;
        } catch (err) {
            console.error('[AssetManager] Error loading draft:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load draft via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    async deleteDraft(draftName: string) {
        logger.debug('[AssetManager] Deleting draft:', draftName);
        try {
            const result = await deleteDraftApi(draftName);
            logger.debug('[AssetManager] Draft deleted successfully:', result);
            return result;
        } catch (err) {
            console.error('[AssetManager] Error deleting draft:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete draft via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    async fetchScheduleEntriesForDraft(draftName: string) {
        logger.debug('[AssetManager] Fetching schedule entries for draft:', draftName);
        try {
            const filters = { draft_name: draftName };
            const rawEntries = await getScheduleEntries(filters);
            logger.debug('[AssetManager] Received draft entries:', rawEntries?.length || 0);

            if (rawEntries && Array.isArray(rawEntries)) {
                const scheduleEntryObjects = convertToScheduleEntries(rawEntries);
                logger.debug('[AssetManager] Transformed draft entries to ScheduleEntry objects:', scheduleEntryObjects.length);
                return scheduleEntryObjects;
            } else {
                console.warn('[AssetManager] No draft entries received or data is not an array. Returning empty array.');
                return [];
            }
        } catch (err) {
            console.error('[AssetManager] Error fetching draft entries:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load draft entries via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    async publishDraftPeriod(draftName: string, periodId: string | number, year: number) {
        logger.debug('[AssetManager] Publishing draft period:', { draftName, periodId, year });
        try {
            const result = await publishDraftPeriodApi(draftName, periodId, year);
            logger.debug('[AssetManager] Draft period published successfully:', result);
            return result;
        } catch (err) {
            console.error('[AssetManager] Error publishing draft period:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to publish draft period via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    // --- Schedule Entry Management Methods ---

    /**
     * Creates a new schedule entry.
     * @param {ScheduleEntry} scheduleEntry - The ScheduleEntry object to create.
     * @returns {Promise<ScheduleEntry>} - A promise that resolves to the created ScheduleEntry object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async createScheduleEntry(scheduleEntry: any): Promise<ScheduleEntry> {
        logger.debug('[AssetManager] Creating schedule entry...');
        logger.debug('[AssetManager] Input scheduleEntry:', scheduleEntry);
        logger.debug('[AssetManager] Input scheduleEntry type:', typeof scheduleEntry);
        logger.debug('[AssetManager] Input scheduleEntry instanceof ScheduleEntry:', scheduleEntry instanceof ScheduleEntry);
        
        try {
            const entryData = scheduleEntry instanceof ScheduleEntry 
                ? scheduleEntry.toObject() 
                : scheduleEntry;
            
            logger.debug('[AssetManager] entryData after toObject():', entryData);
            logger.debug('[AssetManager] entryData keys:', Object.keys(entryData));
            
            // Create a clean API payload with only the fields the backend expects
            // Normalize possible object shapes coming from ScheduleEntry.toObject()
            const subjectNameFromAny = (() => {
                const s: any = (entryData as any).subject;
                if (!s) return entryData.subject_name || entryData.course || entryData.course_name || '';
                if (typeof s === 'string') return s;
                if (typeof s === 'object') return s.name || '';
                return '';
            })();
            const teacherNameFromAny = (() => {
                // For events, prefer host field; for classes, use teacher field
                const entryType = entryData.type || entryData.entry_type || 'class';
                if (entryType === 'event') {
                    // For events, use host field if available
                    if (entryData.host) return entryData.host;
                    // Fallback to teacher_name or teacher if host is not set
                    if (entryData.teacher_name) return entryData.teacher_name;
                }
                
                // For classes or when no host is set, use teacher field
                const t: any = (entryData as any).teacher;
                if (!t) return entryData.teacher_name || '';
                if (typeof t === 'string') return t;
                if (typeof t === 'object') return t.name || '';
                return '';
            })();
            const roomNameFromAny = (() => {
                const r: any = (entryData as any).room;
                if (!r) return entryData.room_name || '';
                if (typeof r === 'string') return r;
                if (typeof r === 'object') return r.name || r.room_code || '';
                return '';
            })();
            const subjectIdFromAny = (entryData as any).subject_id ?? ((entryData as any).subject && (entryData as any).subject.id);
            const roomIdFromAny = (entryData as any).room_id ?? ((entryData as any).room && (entryData as any).room.id);
            const teacherIdFromAny = (entryData as any).teacher_id ?? ((entryData as any).teacher && (entryData as any).teacher.id);

            // Derive multi-teacher associations (supports teacher_ids or teachers array) - but NOT for events
            const teacherIdsFromAny: number[] = (() => {
                // Events don't use teacher associations - they use host field stored in teacher_name
                const entryType = entryData.type || entryData.entry_type || 'class';
                if (entryType === 'event') {
                    return [];
                }
                
                const ids: Array<number> = [];
                const anyData: any = entryData as any;
                // Prefer explicit teacher_ids when present
                if (Array.isArray(anyData.teacher_ids)) {
                    anyData.teacher_ids.forEach((v: any) => {
                        const id = typeof v === 'object' && v !== null ? v.id : v;
                        if (id != null) ids.push(Number(id));
                    });
                }
                // Support teachers array (objects or ids)
                else if (Array.isArray(anyData.teachers)) {
                    anyData.teachers.forEach((t: any) => {
                        const id = typeof t === 'object' && t !== null ? t.id : t;
                        if (id != null) ids.push(Number(id));
                    });
                }
                // Fallback to legacy single teacher_id
                else if (teacherIdFromAny != null) {
                    ids.push(Number(teacherIdFromAny));
                }
                // De-duplicate
                return Array.from(new Set(ids.filter((n) => !isNaN(n))));
            })();

            const apiData = {
                // Core identifying fields
                entry_type: entryData.type || entryData.entry_type || 'class',
                
                // Schedule timing fields
                week_series: entryData.week_series || entryData.weekSeries || '',
                year: entryData.year || new Date().getFullYear(),
                day: entryData.day !== undefined ? (entryData.day === '' ? null : entryData.day) : null,
                start_time: entryData.start_time !== undefined ? (entryData.start_time === '' ? null : entryData.start_time) : (entryData.timeStart !== undefined ? (entryData.timeStart === '' ? null : entryData.timeStart) : null),
                end_time: entryData.end_time !== undefined ? (entryData.end_time === '' ? null : entryData.end_time) : (entryData.timeEnd !== undefined ? (entryData.timeEnd === '' ? null : entryData.timeEnd) : null),
                
                // Also include time field if available (backend can parse this)
                ...(entryData.time && { time: entryData.time }),
                
                // Content fields (prefer IDs, include names for denorm/debug)
                ...(subjectIdFromAny && typeof subjectIdFromAny !== 'object' && { subject_id: subjectIdFromAny }),
                subject_name: subjectNameFromAny,
                ...(teacherIdFromAny && typeof teacherIdFromAny !== 'object' && { teacher_id: teacherIdFromAny }),
                teacher_name: teacherNameFromAny,
                ...(roomIdFromAny && typeof roomIdFromAny !== 'object' && { room_id: roomIdFromAny }),
                room_name: roomNameFromAny !== '' ? roomNameFromAny : null,
                // Multi-teacher associations (only for classes, not events)
                ...((entryData.type || entryData.entry_type) !== 'event' && teacherIdsFromAny.length > 0 && { teacher_ids: teacherIdsFromAny }),
                
                // Optional fields
                title: entryData.title || null,
                description: entryData.description || entryData.notes || null,
                location: entryData.location || null,
                exceptions: entryData.exceptions || null,
                
                // Period association
                ...(entryData.period_id && { period_id: entryData.period_id }),
                ...(entryData.timePeriodId && { period_id: entryData.timePeriodId }),
                
                // Group associations (will be processed below)
                groups: entryData.groups || [],
                
                // Draft field if present
                ...(entryData.draft_name && { draft_name: entryData.draft_name })
            };
            
            logger.debug('[AssetManager] Clean apiData created:', apiData);
            
            // Check for required fields
            logger.debug('[AssetManager] Required field check:');
            logger.debug('  - entry_type:', apiData.entry_type);
            logger.debug('  - year:', apiData.year);
            logger.debug('  - week_series:', apiData.week_series);
            logger.debug('  - day:', apiData.day);
            logger.debug('  - start_time:', apiData.start_time);
            logger.debug('  - end_time:', apiData.end_time);
            logger.debug('  - time:', apiData.time);
            
            // Ensure groups field is properly formatted for the API (IDs only)
            if (apiData.groups && Array.isArray(apiData.groups)) {
                apiData.groups = apiData.groups.map((g: any) => (typeof g === 'object' && g !== null ? g.id : g)).filter((id: any) => id != null);
            } else {
                apiData.groups = [];
            }
            
            logger.debug('[AssetManager] Final apiData being sent to API:', apiData);
            logger.debug('[AssetManager] Final apiData keys:', Object.keys(apiData));
            
            // Add detailed logging of the exact request body
            logger.debug('[AssetManager] About to send HTTP request with body:');
            logger.debug('  - Request method: POST');
            logger.debug('  - Request URL: schedule-entries endpoint');
            logger.debug('  - Request body stringify:', JSON.stringify(apiData, null, 2));
            
            // Defensive: ensure no object-shaped association fields leak through
            delete (apiData as any).subject;
            delete (apiData as any).teacher;
            delete (apiData as any).room;

            const newEntryData = await createScheduleEntry(apiData);
            logger.debug('[AssetManager] Created schedule entry:', newEntryData);
            
            // Convert back to ScheduleEntry instance
            const convertedEntries = convertToScheduleEntries([newEntryData]);
            if (convertedEntries.length === 0) {
                throw new Error('Failed to convert created entry to ScheduleEntry instance');
            }
            const entry = convertedEntries[0];
            if (!entry) {
                throw new Error('Converted entry is undefined');
            }
            return entry;
        } catch (err) {
            console.error('[AssetManager] Error creating schedule entry:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to create schedule entry via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Updates an existing schedule entry.
     * @param {number|string} entryId - The ID of the schedule entry to update.
     * @param {ScheduleEntry|Object} scheduleEntry - The updated ScheduleEntry object or data.
     * @returns {Promise<ScheduleEntry>} - A promise that resolves to the updated ScheduleEntry object.
     * @throws {Error} - Throws an error if the API call fails or data processing fails.
     */
    async updateScheduleEntry(entryId: string | number, scheduleEntry: ScheduleEntry): Promise<ScheduleEntry> {
        logger.debug('[AssetManager] Updating schedule entry:', entryId);
        try {
            const entryObj = scheduleEntry instanceof ScheduleEntry ? scheduleEntry : new ScheduleEntry(scheduleEntry as any);

            const subjectId = (entryObj as any).subjectObj?.id;
            const teacherId = (entryObj as any).teacherObj?.id;
            const roomId = (entryObj as any).roomObj?.id;
            const subjectName = entryObj.subject || entryObj.course || '';
            // For events, use host field; for classes, use teacher field
            const teacherName = entryObj.type === 'event' ? (entryObj.host || '') : (entryObj.teacher || '');
            const roomName = entryObj.room || null;

            // Derive multi-teacher IDs from entry object (teachers array or legacy) - but NOT for events
            const teacherIds: number[] = (() => {
                // Events don't use teacher associations - they use host field stored in teacher_name
                if (entryObj.type === 'event') {
                    return [];
                }
                
                const ids: number[] = [];
                const anyObj: any = entryObj as any;
                const teachersArray = Array.isArray(anyObj.teachers) ? anyObj.teachers : (Array.isArray(anyObj.teachersArray) ? anyObj.teachersArray : []);
                if (teachersArray && teachersArray.length > 0) {
                    teachersArray.forEach((t: any) => {
                        const id = typeof t === 'object' && t !== null ? t.id : t;
                        if (id != null) ids.push(Number(id));
                    });
                } else if (teacherId != null) {
                    ids.push(Number(teacherId));
                }
                return Array.from(new Set(ids.filter((n) => !isNaN(n))));
            })();

            // Create a clean API payload with only the fields the backend expects
            const apiData: any = {
                // Core identifying fields
                entry_type: entryObj.type || 'class',
                type: entryObj.type || 'class', // Also include type field for apiService compatibility
                
                // Schedule timing fields
                week_series: entryObj.weekSeries || '',
                year: entryObj.year || new Date().getFullYear(),
                day: entryObj.day ? entryObj.day : null,
                start_time: entryObj.timeStart ? entryObj.timeStart : null,
                end_time: entryObj.timeEnd ? entryObj.timeEnd : null,
                
                // Content fields
                ...(subjectId ? { subject_id: subjectId } : {}),
                subject_name: subjectName,
                ...(teacherId ? { teacher_id: teacherId } : {}),
                teacher_name: teacherName,
                ...(roomId ? { room_id: roomId } : {}),
                room_name: roomName,
                // Multi-teacher associations (only for classes, not events)
                ...(entryObj.type !== 'event' && teacherIds.length > 0 ? { teacher_ids: teacherIds } : {}),
                
                // Optional fields
                title: entryObj.title || null,
                description: entryObj.description || entryObj.notes || null,
                location: entryObj.location || null,
                exceptions: entryObj.exceptions || null,
                
                // Group associations (IDs only)
                groups: Array.isArray(entryObj.groups) ? entryObj.groups.map((g: any) => (typeof g === 'object' && g !== null ? g.id : g)).filter((id: any) => id != null) : [],
                
                // Draft field if present
                ...(entryObj as any).draft_name ? { draft_name: (entryObj as any).draft_name } : {}
            };
            
            
            // Ensure groups field is properly formatted for the API (IDs only)
            if (apiData.groups && Array.isArray(apiData.groups)) {
                apiData.groups = apiData.groups.map((g: any) => (typeof g === 'object' && g !== null ? g.id : g)).filter((id: any) => id != null);
            } else {
                apiData.groups = [];
            }
            
            // Defensive cleanup
            delete apiData.subject;
            delete apiData.teacher;
            delete apiData.room;

            const updatedEntryData = await updateScheduleEntry(entryId, apiData);
            logger.debug('[AssetManager] Updated schedule entry:', updatedEntryData);
            
            // Convert back to ScheduleEntry instance
            const convertedEntries = convertToScheduleEntries([updatedEntryData]);
            if (convertedEntries.length === 0) {
                throw new Error('Failed to convert updated entry to ScheduleEntry instance');
            }
            const updatedEntry = convertedEntries[0];
            if (!updatedEntry) {
                throw new Error('Converted entry is undefined');
            }
            return updatedEntry;
        } catch (err) {
            console.error('[AssetManager] Error updating schedule entry:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to update schedule entry via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Deletes a schedule entry by ID.
     * @param {string|number} entryId - The ID of the schedule entry to delete.
     * @returns {Promise<void>} - A promise that resolves when the entry is deleted.
     * @throws {Error} - Throws an error if the API call fails.
     */
    async deleteScheduleEntry(entryId: string | number): Promise<void> {
        logger.debug('[AssetManager] Deleting schedule entry:', entryId);
        try {
            await deleteScheduleEntry(entryId);
            logger.debug('[AssetManager] Schedule entry deleted successfully:', entryId);
        } catch (err) {
            console.error('[AssetManager] Error deleting schedule entry:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete schedule entry via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Saves the current local state of schedule entries directly to a draft.
     * This ensures that local changes are preserved when working in draft mode.
     * @param {Array<ScheduleEntry>} scheduleEntries - Array of ScheduleEntry objects representing current state.
     * @param {string} draftName - The draft name to save to.
     * @returns {Promise<void>} - A promise that resolves when all entries are saved.
     * @throws {Error} - Throws an error if any save operation fails.
     */
    async saveCurrentStateToDraft(scheduleEntries: any, draftName: string) {
        logger.debug('[AssetManager] Saving current state directly to draft:', draftName, 'with', scheduleEntries.length, 'entries');
        
        if (!scheduleEntries || !Array.isArray(scheduleEntries)) {
            throw new Error('Schedule entries must be an array');
        }

        if (!draftName) {
            throw new Error('Draft name is required');
        }

        try {
            const savePromises: Promise<ScheduleEntry>[] = [];

            for (const entry of scheduleEntries) {
                const entryData = entry instanceof ScheduleEntry ? entry.toObject() : entry;
                
                // Ensure this entry is marked as part of the draft
                entryData.draft_name = draftName;

                // Determine if this is a new entry (ID starting with temp- or item-) or existing
                const isNewEntry = !entryData.id || 
                                 entryData.id.toString().startsWith('temp-') || 
                                 entryData.id.toString().startsWith('item-');

                if (isNewEntry) {
                    // Remove the temporary ID for creation
                    const createData = { ...entryData };
                    delete createData.id;
                    savePromises.push(this.createScheduleEntry(createData));
                } else {
                    // Update existing entry, ensuring it stays in the draft
                    savePromises.push(this.updateScheduleEntry(entryData.id, entryData));
                }
            }

            const results = await Promise.all(savePromises);
            logger.debug('[AssetManager] Successfully saved all entries to draft:', results.length);
            return results;
        } catch (err) {
            console.error('[AssetManager] Error saving state to draft:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to save current state to draft via AssetManager.';
            throw new Error(errorMessage);
        }
    }

    /**
     * Saves the current local state of schedule entries to the backend before saving as draft.
     * This ensures that local changes are persisted before the draft operation.
     * @param {Array<ScheduleEntry>} scheduleEntries - Array of ScheduleEntry objects representing current state.
     * @param {string} draftName - The draft name to save to (optional, for draft mode).
     * @param {Array} changeLog - Optional change log entries to determine what needs to be deleted.
     * @returns {Promise<void>} - A promise that resolves when all entries are saved.
     * @throws {Error} - Throws an error if any save operation fails.
     */
    async saveCurrentScheduleState(scheduleEntries: any, draftName: string | null = null, changeLog: any[] = []) {
        logger.debug('[AssetManager] Saving current schedule state:', scheduleEntries.length, 'entries');
        
        if (!scheduleEntries || !Array.isArray(scheduleEntries)) {
            throw new Error('Schedule entries must be an array');
        }

        try {
            const savePromises: Promise<any>[] = [];

            // Handle deletions first - look for delete actions in the change log
            const deleteActions = changeLog.filter(log => log.action === 'delete');
            for (const deleteAction of deleteActions) {
                const entryIdToDelete = deleteAction.itemId;
                if (entryIdToDelete && !entryIdToDelete.toString().startsWith('temp-') && !entryIdToDelete.toString().startsWith('item-')) {
                    logger.debug('[AssetManager] Deleting entry:', entryIdToDelete);
                    savePromises.push(this.deleteScheduleEntry(entryIdToDelete));
                }
            }

            // Handle creates and updates
            for (const entry of scheduleEntries) {
                const entryData = entry instanceof ScheduleEntry ? entry.toObject() : entry;
                
                // Add draft name if provided
                if (draftName) {
                    entryData.draft_name = draftName;
                }

                // Determine if this is a new entry (ID starting with temp- or no ID) or existing
                const isNewEntry = !entryData.id || 
                                 entryData.id.toString().startsWith('temp-') || 
                                 entryData.id.toString().startsWith('item-');

                if (isNewEntry) {
                    // Remove the temporary ID for creation
                    const createData = { ...entryData };
                    delete createData.id;
                    savePromises.push(this.createScheduleEntry(createData));
                } else {
                    // Update existing entry
                    savePromises.push(this.updateScheduleEntry(entryData.id, entryData));
                }
            }

            const results = await Promise.all(savePromises);
            logger.debug('[AssetManager] Successfully saved all schedule entries:', results.length);
            return results;
        } catch (err) {
            console.error('[AssetManager] Error saving schedule state:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to save current schedule state via AssetManager.';
            throw new Error(errorMessage);
        }
    }
}

export default AssetManager; 